import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Link, useParams } from 'react-router-dom';
import { FixedAmountInput, useDelegateAssetForm } from '@onomy/react-hub';

import ValidatorFooter from './ValidatorFooter';
import { Caption, Desc } from './ValidatorHeader';
import ValidatorNodeHeader from './ValidatorNodeHeader';
import * as Modal from '../styles';
import BackButton from './BackButton';
import StakingModal from './StakingModal';
import StakingTransactionFailedModal from './StakingTransactionFailedModal';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import ValidatorDelegationSuccess from './ValidatorDelegationSuccess';

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 76px;
  padding: 12px;

  background-color: ${props => props.theme.colors.bgHighlightBorder};
  border-radius: 8px;
`;

const InputWrapper = styled.label`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;

  padding: 4px 12px;

  span {
    font-size: 12px;
    font-weight: 500;
    color: ${props => props.theme.colors.textThirdly};
  }

  input {
    background: transparent;
    border: none;

    font-size: 18px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};

    &:focus,
    &:active {
      outline: none;
    }
  }
`;

const MaxButton = styled.button`
  width: 70px;
  height: 100%;

  border: none;
  border-radius: 7px;
  background-color: #2a2837;

  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.highlightYellow};
`;

const ErrorMessage = styled.div`
  margin-top: 10px;

  font-size: 14px;
  color: ${props => props.theme.colors.highlightRed};
  overflow-wrap: break-word;
`;

export default function ValidatorDelegation({
  direction = 'DELEGATE',
}: {
  direction: 'DELEGATE' | 'UNDELEGATE';
}) {
  const { validatorId = '' } = useParams();
  const verb = useMemo(() => (direction === 'DELEGATE' ? 'delegate' : 'undelegate'), [direction]);

  const {
    delegation,
    loadingStatus,
    txStatus,
    txAmount,
    canSubmit,
    formErrors,
    amount,
    amountFixed,
    setAmountFixed,
    toggleMaxAmount,
    submit: onConfirm,
  } = useDelegateAssetForm({
    undelegate: direction === 'UNDELEGATE',
    validatorAddressStr: validatorId,
  });

  if (txStatus.error) {
    return <StakingTransactionFailedModal error={txStatus.error} />;
  }

  if (loadingStatus.error) {
    return <StakingTransactionFailedModal error={loadingStatus.error} />;
  }

  if (txStatus.pending || loadingStatus.pending) {
    return (
      <StakingModal>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <LoadingSpinner />
        </div>
      </StakingModal>
    );
  }

  if (!delegation) return <StakingModal>No Matching Validator</StakingModal>;

  if (txStatus.finished) {
    return (
      <ValidatorDelegationSuccess delegation={delegation} direction={direction} amount={txAmount} />
    );
  }

  return (
    <StakingModal>
      <Modal.StakingWrapper>
        <Link to={`/validators/${validatorId}/`}>
          <BackButton />
        </Link>

        <ValidatorNodeHeader delegation={delegation} />

        <div>
          <Caption style={{ textTransform: 'capitalize' }}>{verb} NOMs</Caption>
          <Desc>
            Enter the amount of NOM you would like to {verb}. Remember, unstaking your NOM will take
            21 days before your NOM is available for other uses.
          </Desc>
          <FieldWrapper>
            <InputWrapper>
              <span style={{ textTransform: 'capitalize' }}>{verb} NOM</span>
              <FixedAmountInput
                value={amountFixed}
                onChangeValue={setAmountFixed}
                meta={amount.asset.meta}
                placeholder="0.00"
              />
            </InputWrapper>
            <MaxButton onClick={() => toggleMaxAmount()}>MAX</MaxButton>
          </FieldWrapper>
          {amountFixed && formErrors?.amountFixed && (
            <ErrorMessage>{formErrors?.amountFixed}</ErrorMessage>
          )}
        </div>
      </Modal.StakingWrapper>
      <ValidatorFooter>
        <Link to={`/validators/${validatorId}`}>
          <Modal.SecondaryButton type="button">Back</Modal.SecondaryButton>
        </Link>
        <Modal.PrimaryButton type="button" onClick={onConfirm} disabled={!canSubmit}>
          Confirm
        </Modal.PrimaryButton>
      </ValidatorFooter>
    </StakingModal>
  );
}
